<!-- View the data of a twin, this is used by TwinManagementDetail to display data -->

<template lang="pug">
smx-form.mb-8(v-if="asset !== undefined")
  smx-form-item(:label="$t('name')", required)
    template(v-if="isEditing && !editMode")
      p.whitespace-pre-wrap {{ name ? name : "-" }}
    .flex(v-else)
      QRInput(v-model="name")

  smx-form-item(:label="$t('description')")
    p.whitespace-pre-wrap(v-if="isEditing && !editMode") {{ description ? description : "-" }}
    //- NOTE: this used to be a textarea, however currently element plus crashes if it is and attaches a textarea to the bottom of the page. 
    //- This is probably related to data loading, but the only solution I found for now is not to use a textarea here :/
    el-input(v-else, v-model="description")
  template(v-for="(prop, index) in asset.properties", :key="prop.uuid")
    smx-form-subtitle(
      v-if="prop.constraint?.settings?.section && prop.constraint?.settings?.section !== asset.properties?.[index - 1]?.constraint?.settings?.section",
      :label="prop.constraint.settings.section"
    ) 
    smx-form-item(
      :label="getTemplatePropertyName(prop)",
      :required="prop.constraint?.settings?.mandatory || false"
    )
      template(v-if="isEditing && !editMode")
        //- Allow add custom Property views
        slot(
          v-if="hasSlot(prop.customId)",
          :name="prop.customId",
          v-bind="{ property: prop, value: propertyObject[prop.uuid], twin: asset }"
        )

        TwinPropertyView(
          v-else,
          :prop="prop",
          :model-value="propertyObject[prop.uuid]"
        )
      template(v-else)
        TwinPropertyInput(
          :prop="prop",
          :disabled="isEditDisabled(prop)",
          :model-value="mergedInputs[prop.uuid]",
          :hide-qr-input="props.hideQrInput",
          @update:model-value="(value: unknown) => (inputs[prop.uuid] = value)"
        )

  smx-form-item(v-if="isEditing", :label="$t('id')")
    p {{ asset.uuid }}
  smx-form-item(v-if="isEditing", :label="$t('createdAt')")
    p {{ formatDateTime(asset.created) }}
  smx-form-item(v-if="isEditing", :label="$t('lastEdit')")
    p {{ lastEditDate ? formatDateTime(lastEditDate) : "-" }}

  smx-form-item(v-if="knowledgeUnit", :label="$t('activate')")
    el-checkbox(v-model="activateKnowledgeUnitOnCreate")

  smx-form-item(
    v-if="commentVisible",
    :label="$t('comment')",
    :required="commentRequired"
  )
    el-input(v-model="comment")

  template(#actions) 
    el-button(
      v-if="editMode || !isEditing",
      :disabled="saveLoading",
      @click="cancel"
    ) {{ $t("cancel") }}
    //- NOTE: disabled since this won't work with current data initialization
    //- el-button(v-if="!isEditing" v-loading="saveLoading" type="primary" @click="saveChanges(false)") {{ $t('saveAndCreateAnother') }}
    el-button(
      v-if="!isEditing && !props.hideBatchCreate",
      v-loading="saveLoading",
      type="primary",
      @click="showCreateMultipleDialog"
    ) {{ $t("saveMultipleInstances") }}
    el-popconfirm(
      v-if="!hideDeleteTwin && editMode && isEditing",
      :title="$t('confirmDelete')",
      :confirm-button-text="$t('delete')",
      :cancel-button-text="$t('cancel')",
      width="220",
      @confirm="deleteTwin"
    )
      template(#reference)
        el-button(v-loading="saveLoading", type="danger") {{ $t("delete") }}
    el-button(
      v-if="editMode || !isEditing",
      v-loading="saveLoading",
      type="primary",
      @click="saveChanges()"
    ) {{ $t("save") }}

el-dialog(
  v-model="createMultipleDialogVisible",
  width="500",
  :title="$t('saveMultipleInstances')"
)
  template(v-if="createMultipleDialogLoading")
    p {{ $t("nOfmInstancesCreated", { n: instancesCreated, m: createMultipleAmount }) }}
  template(v-else)
    p.mb-8 {{ $t("chooseAmountOfInstances") }}
    .flex.flex-col.ml-1.mb-8
      label.font-bold {{ $t("amount") }}
      el-input-number(v-model="createMultipleAmount", :min="1", :max="10")
  template(#footer)
    .flex.justify-end(v-if="!createMultipleDialogLoading")
      el-button(
        type="danger",
        @click="() => (createMultipleDialogVisible = false)"
      ) {{ $t("cancel") }}
      el-button(type="primary", @click="saveMultipleInstances") {{ $t("create") }}
</template>

<script lang="ts" setup>
import QRInput from "@shared/components/elements/QRInput.vue";
import {
  type TwinProperty,
  type Property,
  type TwinResponse,
  createTwinWithProperties,
} from "@shared/services/twinManagement";
import { useTwinEditCreateStore } from "@shared/stores/twinCreateEdit";
import { useTwinStore } from "@shared/stores/twinManagement";
import { formatDateTime } from "@shared/util/formatting";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import dayjs from "dayjs";
import { ElNotification } from "element-plus";
import { ref } from "vue";
import { useSlots } from "vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import TwinPropertyInput from "./TwinPropertyInput.vue";
import TwinPropertyView from "./TwinPropertyView.vue";
import { type TwinManagementSettings } from "../types";
import { isNil } from "lodash";
import { applicationLanguage } from "@shared/util/languages";

const props = defineProps<{
  editMode: boolean;
  isEditing: boolean;
  commentVisible?: boolean;
  commentRequired?: boolean;
  commentRequiredOnEdit?: boolean;
  hideDeleteTwin?: boolean;
  hideBatchCreate?: boolean;
  hideQrInput?: boolean;
  // emit save instead of actually saving
  overrideSave?: boolean;
  settings?: TwinManagementSettings;
  twin?: TwinResponse;
}>();

const { t } = useI18n();
const emit = defineEmits([
  "endEdit",
  "created",
  "cancel",
  "deleteTwin",
  "multipleCreated",
  "save",
]);
const route = useRoute();

const twinEditCreateStore = useTwinEditCreateStore();
const propertyObject = computed<Record<string, unknown>>(() => {
  if (props.twin) {
    const properties = props.twin.properties?.map((property) => [
      property.uuid,
      property.propertyValue?.value,
    ]);
    return properties ? Object.fromEntries(properties) : {};
  }
  return twinEditCreateStore.propertyObject;
});
const asset = computed(() => props.twin ?? twinEditCreateStore.asset);
const mergedInputs = computed(() =>
  props.twin ? propertyObject.value : twinEditCreateStore.mergedInputs,
);
const assetTemplate = computed(() => twinEditCreateStore.assetTemplate);
const inputs = computed(() => twinEditCreateStore.inputs);
const twinStore = useTwinStore();

function getTemplatePropertyName(prop: TwinProperty) {
  const templatePropertyUuid = prop.propertyTemplateUuid ?? prop.uuid;
  return (
    assetTemplate.value?.properties?.find(
      (templateProperty) => templateProperty.uuid === templatePropertyUuid,
    )?.name ?? prop.name
  );
}

const name = computed({
  get() {
    if (props.twin) {
      return props.twin.name;
    }
    return !isNil(twinEditCreateStore.name)
      ? twinEditCreateStore.name
      : asset.value?.name;
  },
  set(value) {
    twinEditCreateStore.name = value ?? "";
  },
});

const description = computed({
  get() {
    if (props.twin) {
      return props.twin.description ?? "";
    }
    return (
      (twinEditCreateStore.description !== null
        ? twinEditCreateStore.description
        : asset.value?.description) ?? ""
    );
  },
  set(value) {
    twinEditCreateStore.description = value;
  },
});

const activateKnowledgeUnitOnCreate = ref(
  props.settings?.knowledgeUnit?.activateUnit || false,
);

function isEditDisabled(prop: Property) {
  return (
    prop.constraint?.settings?.notUserEditable &&
    !(!props.isEditing && prop.constraint?.settings.editableOnCreate)
  );
}

const comment = ref("");

const slots = useSlots();

const lastEditDate = computed(() => {
  if (asset.value!.properties?.length === 0) {
    return asset.value!.modified ?? null;
  }
  return asset
    .value!.properties?.map((prop) =>
      prop.propertyValue?.modified ? dayjs(prop.propertyValue.modified) : null,
    )
    .reduce((agg, curr) => {
      if (curr) {
        if (agg) {
          return agg.isAfter(curr) ? agg : curr;
        } else {
          return curr;
        }
      }
      return agg;
    }, dayjs(asset.value!.modified));
});

function hasSlot(name?: string) {
  if (name === undefined) {
    return false;
  }
  return !!slots[name];
}

// Saving

// Knowledge unit
const knowledgeUnit = computed(() => {
  if (props.twin) {
    return undefined;
  }
  const spaceId = route.query.space as string;
  const kUnitVersion = route.query.kUnitVersion as string;

  if (spaceId || kUnitVersion) {
    return {
      spaces: [spaceId],
      newVersionOfUnit: kUnitVersion,
    };
  }
  return undefined;
});

// returns an array of all mandatory fields that are not filled in.
const requiredMandatoryFields = computed(() => {
  const requiredFields = [];
  // name is an extra field, all other fields are properties
  if (name.value === "") {
    requiredFields.push(t("name"));
  }
  if (props.isEditing && props.commentRequiredOnEdit && comment.value === "") {
    requiredFields.push(t("comment"));
  }
  (asset.value?.properties || [])
    .filter((item) => item.constraint?.settings?.mandatory)
    .forEach((item) => {
      const value = mergedInputs.value[item.uuid];
      if (value === undefined || value === null || value === "") {
        requiredFields.push(item.name);
      }
    });
  return requiredFields;
});

const saveLoading = ref(false);

// NOTE: input values might have different types or even be null, which is not accepted by the backend.
// Thus before saving we have to adjust types and remove null values
const inputValuesForSaving = computed(() => {
  function getAssetValueForSaving(
    property: TwinProperty | undefined,
    value: string | Array<{ uuid: string }> | string[] | boolean,
  ): string | string[] | number {
    if (typeof value === "boolean") {
      return value.toString();
    }
    if (property === undefined) {
      return "";
    }
    if (Array.isArray(value) && value.length > 0) {
      if (property.type === "twinRelation") {
        return value.map((item) =>
          typeof item === "string" ? item : item.uuid,
        );
      } else {
        // Currently only twinRelation supports array, in case of a user array
        return value as string[];
      }
    }
    // NOTE: typescript doesn't check the `typeof if` clause earlier
    return value as string;
  }

  return (
    Object.keys(inputs.value)
      // Filter undefined values
      .filter((key) => inputs.value[key] !== undefined)
      // Filter fields not editable by the user
      .map((propId) => {
        const property = asset.value!.properties?.find(
          (prop) => prop.uuid === propId,
        );
        const value = inputs.value[propId];

        // For float properties, send null when value is empty to avoid backend validation errors
        if (
          property?.type === "float" &&
          (value === null || value === "" || value === undefined)
        ) {
          return {
            propertyUuid: propId,
            value: null,
          };
        }

        return {
          // NOTE: due to https://github.com/inctec/smx-portal/issues/146 null values are replaced with empty strings for non-float properties
          propertyUuid: propId,
          value: getAssetValueForSaving(property, value !== null ? value : ""),
        };
      })
  );
});

async function saveChanges() {
  if (requiredMandatoryFields.value.length > 0) {
    ElNotification({
      title: t("error"),
      message: `${t("followingFieldsAreRequired")} ${requiredMandatoryFields.value.join(", ")}`,
      type: "error",
    });

    return;
  }

  if (props.overrideSave) {
    emit("save", inputValuesForSaving.value);
    return;
  }

  saveLoading.value = true;
  try {
    if (props.isEditing) {
      await twinStore.editTwin(asset.value!, {
        comment: comment.value,
        twinName: twinEditCreateStore.name
          ? twinEditCreateStore.name
          : undefined,
        twinDescription: twinEditCreateStore.description
          ? twinEditCreateStore.description
          : undefined,
        properties: inputValuesForSaving.value,
      });
      emit("endEdit");
      ElNotification({
        title: t("success"),
        message: t("changesSaved"),
        type: "success",
      });
    } else {
      const response = await createTwinWithProperties({
        name: name.value!,
        description: description.value,
        type: asset.value!.type,
        namespace: asset.value!.namespace,
        language: asset.value!.language ?? applicationLanguage.value,
        initial: asset.value!.initial,
        version: asset.value!.version ?? 0,
        active: true,
        locked: false,
        knowledgeUnit: {
          ...(props.settings?.knowledgeUnit ?? {}),
          activateUnit: activateKnowledgeUnitOnCreate.value,
          ...knowledgeUnit.value,
        },
        isTemplate: false,
        templateUuid: asset.value!.uuid,
        properties: inputValuesForSaving.value
          .map((propData) => {
            const customId = asset.value!.properties!.find(
              (templateProp) => templateProp.uuid === propData.propertyUuid,
            )?.customId;
            return {
              property: customId,
              value: propData.value,
            };
          })
          .filter(
            (item) => item.property !== undefined && item.property !== null,
          ) as { property: string; value: string | number | string[] }[],
      });
      emit("created", response);
    }
  } catch (e: unknown) {
    showResponseErrorToUser(e, t);
    throw e;
  } finally {
    saveLoading.value = false;
  }

  // TODO: this is okay for now but will have the bug that if cancelling after saving changes, the originial values will be visible, should reload or merge locally
  // inputs.value = {};
}

function cancel() {
  emit("cancel");
}

// Create multiple instances
const createMultipleDialogVisible = ref(false);
const createMultipleAmount = ref(1);

function showCreateMultipleDialog() {
  if (requiredMandatoryFields.value.length > 0) {
    ElNotification({
      title: t("error"),
      message: `${t("followingFieldsAreRequired")} ${requiredMandatoryFields.value.join(", ")}`,
      type: "error",
    });

    return;
  }
  createMultipleDialogVisible.value = true;
  createMultipleAmount.value = 1;
}

const instancesCreated = ref(0);

const createMultipleDialogLoading = ref(false);

async function saveMultipleInstances() {
  createMultipleDialogLoading.value = true;
  for (let index = 0; index < createMultipleAmount.value; index++) {
    await createTwinWithProperties({
      name: name.value!,
      description: description.value,
      type: asset.value!.type,
      namespace: asset.value!.namespace,
      language: asset.value!.language ?? applicationLanguage.value,
      initial: asset.value!.initial,
      version: asset.value!.version ?? 0,
      active: true,
      locked: false,
      knowledgeUnit: knowledgeUnit.value,

      isTemplate: false,
      templateUuid: asset.value!.uuid,
      properties: inputValuesForSaving.value
        .map((propData) => {
          const customId = asset.value!.properties!.find(
            (templateProp) => templateProp.uuid === propData.propertyUuid,
          )?.customId;
          return {
            property: customId,
            value: propData.value,
          };
        })
        .filter(
          (item) => item.property !== undefined && item.property !== null,
        ) as { property: string; value: string | number | string[] }[],
    })
      .then(() => {
        instancesCreated.value += 1;
      })
      .then(
        () => {},
        (e) => {
          showResponseErrorToUser(e, t);
        },
      );
  }
  createMultipleDialogLoading.value = false;
  ElNotification({
    title: t("success"),
    message: t("entryAdded"),
    type: "success",
  });
  emit("multipleCreated");
}

function deleteTwin() {
  emit("deleteTwin");
}
</script>
